import { ProductName } from '@pypestream/api-services';
import {
  Divider,
  NavControls,
  NavExtra,
  NavLinks,
  TextSubtitle,
  UniversalNav,
  UniversalNavChangelog,
  UniversalNavHelp,
  UniversalUserDropdown,
  UniversalUserDropdownTrigger,
  Tooltip,
  Popover,
  TextOverline,
  IconButton,
  Stack,
  BlockList,
  Spacer,
  Button,
} from '@pypestream/design-system';
import {
  TranslationComponent,
  useTranslation,
  type TranslationKeys,
} from '@pypestream/translations';
import {
  isErrorReportingActive,
  subscribeToSentryReporting,
} from '@pypestream/utils';
import { sortBy } from 'lodash';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';

import {
  ActiveSolutionUsersAvatars,
  ActiveSolutionUsersListContent,
  PublishButton,
  useDeleteSolutionModal,
  usePublishModal,
  useReportBugModal,
} from '..';
import {
  ProStudioPages,
  useCurrentRoute,
  useLoadingState,
  useAnalytics,
  useProStudioRoutes,
  useSolutionPresence,
  useProStudioViewerRole,
} from '../../hooks';
import {
  sendProStudioEvent,
  sendUserEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { useSolutionTypePopover } from '../popovers';
import { ValidationPopover } from './validation-popover';
import { VALIDATION_INCOMPLETE_FALLBACK } from './validation-popover.utils';
import {
  NodeColumns,
  tableStore,
  ValidationMode,
} from '../../stores/table-store';
import { readAgentCompilerMode } from '../../utils/agent-compiler-mode';
import { readDraftLabelSuffix } from '../../utils/build-selected-solution';
import { isReportBugEnabled } from '../../utils/report-bug-flag';
import { SolutionsDropdown } from './solutions-dropdown';
import { NavLinkWrapper } from './nav-link-wrapper';
import { NavLinksOverflow } from './nav-links-overflow';
import { usePendingPublish } from './use-pending-publish';
import { findNodeNumberProblem } from '../../stores/utils';

import './primary-nav.scss';
import { SolutionUserPresenceItem } from '../../xstate/prostudio.xstate.types';

type NavItem = {
  path: string;
  label: string;
  hidden: boolean;
  disabled?: boolean;
  i18nKey?: string;
};

const parsedChangelogId = Number(
  import.meta.env.FE_PRODUKTLY_PRO_STUDIO_CHANGELOG_ID
);
const changelogId =
  Number.isFinite(parsedChangelogId) && parsedChangelogId > 0
    ? parsedChangelogId
    : undefined;

export const PrimaryNav: FC = observer(() => {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { pathname } = useLocation();
  const {
    draftVersionId,
    selectedBot,
    selectedProject,
    userInfo,
    // @todo: remove this once we have a proper way to get the account id on pro studio
    projectAccountId,
    agentCompilerMode,
    reportBugEnabled,
    solutionUserPresences,
    presencePagePath,
    projectUsers,
  } = useProStudioCtxSelector((ctx) => ({
    selectedBot: ctx.selectedBot,
    draftVersionId: ctx.draftVersion,
    selectedProject: ctx.project?.id,
    userInfo: ctx.userInfo,
    projectAccountId: ctx.project?.accountId,
    agentCompilerMode: readAgentCompilerMode(ctx.featureFlags),
    reportBugEnabled: isReportBugEnabled(ctx.featureFlags),
    solutionUserPresences: ctx.solutionUserPresences,
    presencePagePath: ctx.presencePagePath,
    projectUsers: ctx.projectUsers,
  }));

  const isViewer = useProStudioViewerRole();
  const { t } = useTranslation();

  const isAutomaticValidation =
    tableStore.validationMode === ValidationMode.Auto;
  const [isValidating, setIsValidating] = useState(false);
  const botType = selectedBot?.botType;
  const isLoading = useLoadingState();
  const isValidationProcess = isAutomaticValidation
    ? tableStore.isValidating
    : isValidating;
  const isProcessing = isLoading || isValidationProcess;
  const routes = useProStudioRoutes();
  const {
    publishModal,
    openModal: openPublishModal,
    closeModal: closePublishModal,
  } = usePublishModal({
    draftVersionId: draftVersionId ?? undefined,
  });

  const { solutionVersionPublishingInProgress } = tableStore;

  useEffect(() => {
    if (solutionVersionPublishingInProgress) {
      closePublishModal();
    }
  }, [solutionVersionPublishingInProgress, closePublishModal]);
  const { deleteSolutionModal, openModal: openDeleteSolutionModal } =
    useDeleteSolutionModal();
  // Two conditions, and they answer different questions. The LaunchDarkly flag
  // says whether this environment offers the channel at all; the second says
  // whether a report would actually reach Sentry rather than be discarded.
  //
  // SUBSCRIBED, not read once, and both halves of that answer move after this
  // component first renders. The client appears late — `sentry-gate.ts` waits
  // for LaunchDarkly before calling `Sentry.init`, which is after the app has
  // rendered — so a one-off read is `false` on every first paint and would
  // leave the button permanently hidden. The kill switch can then close at any
  // later moment, which is the second move.
  const reportingActive = useSyncExternalStore(
    subscribeToSentryReporting,
    isErrorReportingActive
  );
  const canReportBug = reportBugEnabled && reportingActive;
  // The gate goes INTO the hook rather than around its element: a form the
  // user already opened must survive the gate closing (see `enabled`).
  const { reportBugModal, openReportBugModal } = useReportBugModal({
    enabled: canReportBug,
  });
  // One string for both channels: the tooltip names the button for sighted
  // users and `label` gives it its accessible name, and the two must not drift.
  const reportBugLabel =
    t('pro-studio/navigation:report_bug_modal.title') ?? '';
  const { solutionTypePopover, solutionTypePopoverTriggerButton } =
    useSolutionTypePopover({
      disabled: isProcessing,
      onClose: openDeleteSolutionModal,
    });

  const isHomePage = useCurrentRoute().label === routes.home.label;
  const isConfigurationsPage =
    useCurrentRoute().label === ProStudioPages.CONFIGURATIONS;

  const projectSettingsBasePath = selectedProject
    ? `/${selectedProject}/settings`
    : '';
  const isProjectSettingsPage =
    !!projectSettingsBasePath && pathname.startsWith(projectSettingsBasePath);

  // Project Settings behaves like a first-level (project-scoped) page: it shows
  // the project tabs and hides all solution-scoped controls.
  const isFirstLevelPage =
    isHomePage || isConfigurationsPage || isProjectSettingsPage;
  const solutionId = selectedBot?.uniqueId;

  useSolutionPresence(isFirstLevelPage, solutionId, pathname);

  const navItems: NavItem[] = [
    // {
    //  ...routes.configurations,
    //  hidden: !selectedProject,
    //  label: 'Project Configurations',
    // },
    {
      ...routes.versions,
      hidden: isFirstLevelPage,
      i18nKey: 'pro-studio/navigation:nav_links.versions',
    },
    {
      ...routes.channels,
      hidden:
        isFirstLevelPage || !selectedBot || !botType || botType === 'survey',
      i18nKey: 'pro-studio/navigation:nav_links.channels',
    },
    {
      ...routes.default_messages,
      hidden: isFirstLevelPage,
      i18nKey: 'pro-studio/navigation:nav_links.default_messages',
    },
  ].filter((item) => item.hidden === false);

  // The old centered "Solutions / Project Settings" tabs were replaced by the
  // left side-nav (ProjectAreaSidenav), which now spans the Solutions list and
  // all settings pages. See project-area-sidenav.tsx.

  const solutionItems: NavItem[] = [
    {
      ...routes.workflows,
      hidden: isFirstLevelPage,
      // disabled: !draftVersionId,
      i18nKey: 'pro-studio/navigation:nav_links.workflows',
    },
    {
      ...routes.files,
      hidden: isFirstLevelPage,
      // Deliberately NOT `disabled: !draftVersionId` like the Actions entry it replaces:
      // the Media tab reads ctx.selectedBot.assets and works without a draft version, so
      // gating the whole nav item here would make Media unreachable.
      i18nKey: 'pro-studio/navigation:nav_links.files',
    },
  ].filter((item) => item.hidden === false);

  // Page-scoped: only coworkers viewing the SAME page appear in the nav list
  // (the name says so). PSTUDIO-3776: a "simplify" refactor (e646130517)
  // dropped this `pagePath` filter as "unused", regressing the nav to
  // solution-scoped — an off-page coworker wrongly showed up here. Restored.
  const currentPagePath = presencePagePath ?? pathname;
  const viewingListForCurrentPage = useMemo(() => {
    const list = solutionUserPresences ?? [];
    const isCurrentUser = (p: { user?: { id?: string } }) => {
      if (String(p.user?.id) === String(userInfo?.id)) return true;
    };
    const active = list.filter(
      (p) => p.pagePath === currentPagePath && p.isActive && !isCurrentUser(p)
    );
    const sortKey = (p: SolutionUserPresenceItem) => {
      const first = p.user?.firstName ?? '';
      const last = p.user?.lastName ?? '';
      const name = [first, last].filter(Boolean).join(' ').toLowerCase();
      return name || String(p.userId ?? p.id ?? '');
    };
    return sortBy(active, sortKey);
  }, [solutionUserPresences, currentPagePath, userInfo?.id]);

  const handleAvatarClick = useCallback(
    (presence: SolutionUserPresenceItem) => {
      const focusedCell = presence.pageParams?.focusedCell;
      if (!focusedCell) return;

      const { rowId, columnId } = focusedCell;
      const presencePath = presence.pagePath;
      let targetPath: string | null = null;
      if (presencePath.includes('/workflows')) {
        targetPath = routes.workflows.path;
      } else if (presencePath.includes('/files')) {
        targetPath = `${routes.files.path}/actions`;
      }
      if (!targetPath) return;

      const isSamePage = pathname === targetPath;
      if (isSamePage) {
        tableStore.scrollToAndFocusCellById(rowId, columnId as NodeColumns);
      } else {
        tableStore.setPendingScrollToCellByRowId({
          rowId,
          columnId: columnId as NodeColumns,
        });
        navigate(targetPath);
      }
    },
    [pathname, routes.workflows.path, routes.files.path, navigate]
  );

  const handleValidate = useCallback(async () => {
    setIsValidating(true);
    await tableStore.validateAndSync(ValidationMode.Manual);
    setIsValidating(false);
  }, []);

  // PSTUDIO-4242: a Publish click during a validation pass is queued, not lost.
  // Only a pass that can produce a verdict counts: in Manual mode that is the
  // Validate button's own pass — an edit's bare `validateAndSync()` returns the
  // cached verdict there, so a click queued behind it could only be refused.
  const isPublishWaitingOnValidation = isValidationProcess;
  const [validationPopoverOpenRequest, setValidationPopoverOpenRequest] =
    useState(0);
  const pendingPublish = usePendingPublish({
    isBusy: isPublishWaitingOnValidation,
    // The page scope too: on a first-level page the button is gone, and a
    // click queued before navigating there must not open the modal over it.
    resetKey: isFirstLevelPage ? undefined : solutionId,
    canPublishNow: () =>
      tableStore.canPublish && tableStore.effectiveErrors.length === 0,
    isStillBusy: () => tableStore.isValidating,
    waitForSaves: () => tableStore.waitForPendingSaves(),
    onReady: () => {
      sendProStudioEvent({ type: 'loadPublishModalData' });
      openPublishModal();
    },
    onBlocked: () => {
      // A coworker's publish began while the click waited: the button's own
      // tooltip says so, and the errors popover would answer the wrong question.
      if (tableStore.solutionVersionPublishingInProgress) return;
      setValidationPopoverOpenRequest((n) => n + 1);
    },
  });

  if (!userInfo?.defaultOrgId) {
    return null;
  }

  // PSTUDIO-4242: the running pass no longer disables the button — the click
  // is queued and judged by `canPublish` once the pass ends. `canPublish` is
  // skipped only DURING a pass, because what it reads then (errors, the
  // failure flag, the manual-mode dirty flag) is the verdict the pass is about
  // to replace. Everything that is not validation still disables it — and so
  // does a node-number problem, which is derived from the current rows and no
  // pass can clear (PSTUDIO-4000 / 3990).
  const isPublishButtonDisabled =
    isLoading ||
    solutionVersionPublishingInProgress ||
    (isPublishWaitingOnValidation
      ? findNodeNumberProblem(tableStore.rowsForValidation) !== null
      : !tableStore.canPublish);

  const publishButtonTooltipContent = solutionVersionPublishingInProgress
    ? t('pro-studio/navigation:publish_button.tooltip.publishing_in_progress', {
        defaultValue: 'Publishing is in progress.',
      })
    : // PSTUDIO-4242: deliberately no tooltip for "validating" or "a click is
      // queued" — the button shows that itself (`loading`, `aria-busy`).
      // `PublishButton` renders a bare button or a Tooltip-wrapped one, and
      // switching between the two remounts it: at the pass start that lands
      // between pointerdown (whose blur commit STARTS the pass) and click, so
      // the click hit a detached node and was lost again.
      !isPublishButtonDisabled
      ? undefined
      : // PSTUDIO-4120: "fix any errors" is a false instruction in the new
        // state — the pass produced no verdict, so both error lists can be
        // EMPTY and there is nothing highlighted to fix. Reuses the popover's
        // key rather than adding a second string for the same fact.
        tableStore.validationFailure !== null
        ? t('pro-studio/navigation:validation_popover.incomplete_state', {
            defaultValue: VALIDATION_INCOMPLETE_FALLBACK,
          })
        : isAutomaticValidation
          ? t('pro-studio/navigation:publish_button.tooltip.fix_errors', {
              defaultValue: 'Fix any errors to enable publishing.',
            })
          : t(
              'pro-studio/navigation:publish_button.tooltip.validate_then_fix',
              {
                defaultValue:
                  'Click Validate to check your changes, then fix any errors to enable publishing.',
              }
            );

  const isValidateButtonDisabled =
    isProcessing ||
    tableStore.isValidating ||
    !tableStore.canValidateInManualMode;

  const validateButtonTooltipContent = isValidateButtonDisabled
    ? t('pro-studio/navigation:validate_button.tooltip.no_changes', {
        defaultValue: 'There are no changes to validate.',
      })
    : t('pro-studio/navigation:validate_button.tooltip.check_solution', {
        defaultValue: 'Check your solution for errors.',
      });

  return (
    <>
      <UniversalNav
        app={ProductName.ProStudio}
        slot="nav"
        orgId={projectAccountId}
        projectId={selectedProject}
        onManagerToolClick={(path) => {
          if (path.startsWith('http')) {
            window.location.href = path;
          } else {
            navigate(path);
          }
        }}
        onSelectionChange={(orgId) => {
          if (orgId) {
            // if (selectedOrgId && !selectedProject) {
            //   navigate(pathname.replace(selectedOrgId, orgId));
            // } else if (selectedProject) {
            //   navigate(`/organization/${orgId}/projects`);
            // } else {
            //   navigate(`/organization/${orgId}`);
            // }
          }
        }}
      >
        <SolutionsDropdown />
        {/* minWidth:0 lets the grid `links` (1fr) track shrink so the overflow
            hook can measure the real available width (default min-width:auto
            would keep the track at content size → tabs never collapse). */}
        <NavLinks slot="navigation" style={{ minWidth: 0 }}>
          <NavLinksOverflow items={navItems} pathname={pathname} />
        </NavLinks>

        {!isFirstLevelPage && viewingListForCurrentPage.length > 0 && (
          <NavExtra slot="extra">
            <div className="c-primary-nav__viewing-list">
              <ActiveSolutionUsersAvatars
                presences={viewingListForCurrentPage}
                projectUsers={projectUsers}
                variant="inline"
                popoverTriggerId="viewers"
                onAvatarClick={handleAvatarClick}
              />
            </div>
            <Popover width="auto" trigger="viewers" test-id="viewers-popover">
              <TextOverline variant="tertiary">
                Viewing this solution
              </TextOverline>
              <Spacer></Spacer>
              <BlockList gutter="large">
                <ActiveSolutionUsersListContent
                  presences={viewingListForCurrentPage}
                  projectUsers={projectUsers}
                  onItemClick={handleAvatarClick}
                />
              </BlockList>
            </Popover>
          </NavExtra>
        )}
        {!isFirstLevelPage && (
          <>
            <NavControls slot="controls">
              <Stack nowrap alignItems="center" gutter="xsmall">
                {tableStore.validationMode === ValidationMode.Manual && (
                  <Tooltip content={validateButtonTooltipContent}>
                    <Button
                      test-id="ps-validate-btn"
                      variant="secondary"
                      size="small"
                      disabled={isValidateButtonDisabled}
                      onClick={handleValidate}
                    >
                      Validate
                    </Button>
                  </Tooltip>
                )}
                <ValidationPopover
                  isLoading={isProcessing}
                  errors={tableStore.effectiveErrors}
                  agentCompilerMode={agentCompilerMode}
                  isViewer={isViewer}
                  openErrorsRequest={validationPopoverOpenRequest}
                />
                <TextSubtitle
                  test-id="ps-draft-label"
                  size="small"
                  variant="secondary"
                  truncate
                >
                  <TranslationComponent i18nKey="pro-studio/common:versions.draft">
                    Draft
                  </TranslationComponent>{' '}
                  {draftVersionId ? readDraftLabelSuffix(draftVersionId) : 'v-'}
                </TextSubtitle>
              </Stack>
              <Divider vertical />
              {solutionItems.map(({ path: to, label, i18nKey, disabled }) => (
                <NavLinkWrapper
                  label={label}
                  key={label}
                  to={to}
                  isActive={pathname === to}
                  i18nKey={i18nKey as TranslationKeys}
                  disabled={disabled}
                >
                  {label}
                </NavLinkWrapper>
              ))}

              {!isViewer && (
                <PublishButton
                  disabled={isPublishButtonDisabled}
                  validating={isPublishWaitingOnValidation}
                  pending={pendingPublish.isPending}
                  onClick={pendingPublish.request}
                  tooltipContent={publishButtonTooltipContent}
                />
              )}
            </NavControls>

            {!isViewer && (
              <>
                <span slot="actions">{solutionTypePopoverTriggerButton}</span>
                {solutionTypePopover}
              </>
            )}
          </>
        )}

        {changelogId !== undefined && (
          <UniversalNavChangelog slot="actions" changelogId={changelogId} />
        )}
        {/*
          Hidden where a report could not leave the browser. Which deploys have
          a Sentry client is decided in LaunchDarkly, and without one the SDK
          returns an id for an event it discards — so the form would confirm a
          report nobody received (measured 2026-09-20: success screen shown,
          zero envelopes on the wire).
        */}
        {canReportBug && (
          <span slot="actions">
            <Tooltip placement="bottom" content={reportBugLabel}>
              <IconButton
                test-id="ps-report-bug-btn"
                name="alert"
                // `label`, not `aria-label`: the real <button> lives in the
                // component's shadow root, so an `aria-label` written on the
                // <ps-icon-button> tag never reaches it and the button ends up
                // with no accessible name at all — the design system says so
                // itself, on the property this passes.
                label={reportBugLabel}
                hasPopup="dialog"
                onClick={openReportBugModal}
              />
            </Tooltip>
          </span>
        )}
        <UniversalNavHelp slot="actions" />

        <UniversalUserDropdownTrigger slot="actions" />

        <UniversalUserDropdown
          onPreferencesClick={() => {
            const defaultOrgPath = userInfo?.defaultOrgId
              ? `/organization/${userInfo?.defaultOrgId}`
              : '';

            const url = `${import.meta.env.FE_MANAGER_URL}${defaultOrgPath}/my-account`;

            window.location.href = url;
          }}
          onLogout={() => {
            if (analytics && typeof analytics.reset === 'function') {
              analytics.reset();
            }
            sendUserEvent({
              type: 'user.signOut',
            });
          }}
        />
      </UniversalNav>
      {deleteSolutionModal}
      {publishModal}
      {reportBugModal}
    </>
  );
});
